import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { useEffect, useState } from 'react'
import { getConsentPreferences } from './utils/cookie-consent'

export function PHProvider({ children }: { children: React.ReactNode }) {
  const [hydrated, setHydrated] = useState(false)
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false)

  useEffect(() => {
    const preferences = getConsentPreferences()

    // Initialize PostHog with appropriate settings based on consent
    if (preferences.consented && preferences.analytics) {
      // Full tracking mode - user consented to analytics
      posthog.init(import.meta.env.VITE_PH_KEY, {
        api_host: import.meta.env.VITE_PH_HOST,
        person_profiles: 'always',
        persistence: 'localStorage+cookie'
      })
      setAnalyticsEnabled(true)
    } else if (preferences.consented) {
      // Anonymous mode - user explicitly rejected analytics
      // Don't initialize PostHog at all
      setAnalyticsEnabled(false)
    } else {
      // No consent yet - don't initialize PostHog
      // Wait for user to make a choice
      setAnalyticsEnabled(false)
    }

    setHydrated(true)

    // Listen for consent changes
    const handleConsentChange = (event: Event) => {
      const customEvent = event as CustomEvent
      const newPreferences = customEvent.detail

      if (newPreferences.analytics && !analyticsEnabled) {
        // User just enabled analytics - initialize PostHog
        posthog.init(import.meta.env.VITE_PH_KEY, {
          api_host: import.meta.env.VITE_PH_HOST,
          person_profiles: 'always',
          persistence: 'localStorage+cookie'
        })
        setAnalyticsEnabled(true)
      } else if (!newPreferences.analytics && analyticsEnabled) {
        // User disabled analytics - opt out and clear data
        posthog.opt_out_capturing()
        posthog.reset()
        setAnalyticsEnabled(false)
      }
    }

    window.addEventListener('cookieConsentChanged', handleConsentChange)
    return () => window.removeEventListener('cookieConsentChanged', handleConsentChange)
  }, [analyticsEnabled])

  if (!hydrated) return <>{children}</>

  return analyticsEnabled ? <PostHogProvider client={posthog}>{children}</PostHogProvider> : <>{children}</>
}
